if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // SORSO 2026-09-08: the gift app (EasyGift) fires its own /cart/add at the same moment as this
      // one, and Shopify occasionally drops or rejects the second concurrent write. That left buyers
      // with a cart of gifts and no aerator. This wrapper retries a failed add, then verifies the
      // variant is really in the cart before rendering the drawer, and re-adds it if it is not.
      addWithRetry(config, variantId, attempt = 1) {
        return fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status && attempt < 3) {
              return new Promise((r) => setTimeout(r, 700)).then(() => this.addWithRetry(config, variantId, attempt + 1));
            }
            return response;
          })
          .catch((e) => {
            if (attempt < 3) return new Promise((r) => setTimeout(r, 700)).then(() => this.addWithRetry(config, variantId, attempt + 1));
            throw e;
          });
      }

      verifyInCart(variantId, config, response) {
        return fetch(`${routes.cart_url}.js`, { cache: 'no-store' })
          .then((r) => r.json())
          .then((cart) => {
            const present = (cart.items || []).some((i) => String(i.variant_id) === String(variantId));
            if (present) return response;
            // Lost to the race: add once more and re-fetch the drawer sections.
            return this.addWithRetry(config, variantId, 1);
          })
          .catch(() => response);
      }

      // SORSO 2026-09-09: after the product is confirmed in the cart, make sure the gift app's lines survived the
      // concurrent-write race (see window.sorsoGiftGuard in cart-drawer-extras.js). If a gift had to be re-added,
      // re-fetch the drawer sections so the first paint already shows the gifts.
      verifyGifts(variantId, response) {
        if (!response || response.status || !window.sorsoGiftGuard) return Promise.resolve(response);
        return window.sorsoGiftGuard
          .ensure(variantId, { settleMs: 600 })
          .then((g) => (g && g.repaired ? this.refreshSections(response) : response))
          .catch(() => response);
      }

      refreshSections(response) {
        if (!this.cart || !this.cart.getSectionsToRender) return Promise.resolve(response);
        const ids = this.cart.getSectionsToRender().map((s) => s.id).join(',');
        return fetch(`${routes.cart_url}?sections=${ids}`, { cache: 'no-store' })
          .then((r) => r.json())
          .then((sections) => { response.sections = sections; return response; })
          .catch(() => response);
      }

      // SORSO 2026-09-17: instant-open drawer. Visual only: the drawer opens at the tap with an "Adding…" row and is
      // inert (pointer-events off, Checkout disabled) until the real contents land. The cart write chain below
      // (addWithRetry → verifyInCart → verifyGifts → renderContents) is unchanged in order and timing.
      get drawer() {
        return this.cart && this.cart.tagName === 'CART-DRAWER' && typeof this.cart.open === 'function' ? this.cart : null;
      }

      openOptimistic() {
        const d = this.drawer;
        if (!d) return;
        try {
          const inner = d.querySelector('.drawer__inner');
          if (!inner) return;
          const header = inner.querySelector(':scope > .sw-header');
          let row = d.querySelector('.sw-adding-row');
          if (!row) {
            row = document.createElement('div');
            row.className = 'sw-adding-row';
            row.setAttribute('role', 'status');
            if (header) header.insertAdjacentElement('afterend', row);
            else inner.prepend(row);
          }
          const h1 = document.querySelector('.product__title h1') || document.querySelector('h1.product__title') || document.querySelector('h1');
          const title = h1 ? h1.textContent.trim().replace(/\s+/g, ' ') : '';
          const og = document.querySelector('meta[property="og:image"]');
          const mediaImg = document.querySelector('.product__media img, .product-media img, .product__media-item img');
          const img = (mediaImg && (mediaImg.currentSrc || mediaImg.src)) || (og && og.content ? og.content.replace(/^http:/, 'https:') : '');
          let variant = '';
          try {
            const id = String((this.variantIdInput && this.variantIdInput.value) || '');
            const vs = (window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.product && ShopifyAnalytics.meta.product.variants) || [];
            const v = vs.find((x) => String(x.id) === id);
            if (v && v.public_title) variant = v.public_title;
          } catch (e) {}
          const esc = (t) => String(t).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
          row.innerHTML =
            (img ? '<img class="sw-adding__img" src="' + esc(img) + '" alt="">' : '<span class="sw-adding__img"></span>') +
            '<div class="sw-adding__text"><span class="sw-adding__title">' + esc(title) + '</span>' +
            '<span class="sw-adding__sub">Adding' + (variant ? ' ' + esc(variant) : '') + ' to your cart\u2026</span></div>' +
            '<span class="sw-adding__spin" aria-hidden="true"></span>';
          const chk = d.querySelector('#CartDrawer-Checkout');
          if (chk) chk.setAttribute('disabled', 'disabled');
          d.classList.add('sw-adding');
          if (!d.classList.contains('active')) d.open(this.submitButton);
          // Safety net: if the add chain never settles (network hang), un-freeze after 15 s so the customer can retry.
          clearTimeout(this._optTimer);
          this._optTimer = setTimeout(() => {
            if (!d.classList.contains('sw-adding')) return;
            this.closeOptimistic(false);
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            const sp = this.querySelector('.loading__spinner');
            if (sp) sp.classList.add('hidden');
          }, 15000);
        } catch (e) {
          /* cosmetic; never block the add */
        }
      }

      closeOptimistic(keepOpen) {
        clearTimeout(this._optTimer);
        const d = this.drawer;
        if (!d) return;
        d.classList.remove('sw-adding');
        const row = d.querySelector('.sw-adding-row');
        if (row) row.remove();
        if (!keepOpen) {
          const chk = d.querySelector('#CartDrawer-Checkout');
          if (chk && !d.classList.contains('is-empty')) chk.removeAttribute('disabled');
          if (typeof d.close === 'function') d.close();
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');
        this.openOptimistic();

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        const variantId = formData.get('id');

        this.addWithRetry(config, variantId)
          .then((response) => this.verifyInCart(variantId, config, response))
          .then((response) => this.verifyGifts(variantId, response))
          .then((response) => {
            if (response.status) {
              this.closeOptimistic(false);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              });
            this.error = false;
            this.closeOptimistic(true);
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            this.closeOptimistic(false);
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
